import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Runtime public config — the storefront's public third-party identifiers (Razorpay publishable key,
 * Google/Facebook client ids) are no longer hardcoded in environment.ts. They are fetched ONCE at app
 * bootstrap from the backend's IS_PUBLIC `/public-config` endpoint, which the admin manages dynamically
 * through the Integrations module. Secrets never reach the browser — only these public identifiers.
 *
 * Loaded via provideAppInitializer before the app renders, so payment.service / login read a populated
 * value. On failure the values stay blank and callers fall back gracefully (mock checkout / disabled
 * social buttons), exactly as before.
 */
@Injectable({ providedIn: 'root' })
export class RuntimeConfigService {
  private http = inject(HttpClient);

  private _razorpayKeyId = signal('');
  private _razorpayMode = signal('');
  private _googleClientId = signal('');
  private _facebookAppId = signal('');

  readonly razorpayKeyId = this._razorpayKeyId.asReadonly();
  readonly razorpayMode = this._razorpayMode.asReadonly();
  readonly googleClientId = this._googleClientId.asReadonly();
  readonly facebookAppId = this._facebookAppId.asReadonly();

  async load(): Promise<void> {
    try {
      const res = await firstValueFrom(this.http.get<Record<string, unknown>>(`${environment.customApiBase}/public-config`));
      const d = (res && (res as { data?: Record<string, unknown> }).data) || res || {};
      this._razorpayKeyId.set(String((d as Record<string, unknown>)['razorpayKeyId'] ?? ''));
      this._razorpayMode.set(String((d as Record<string, unknown>)['razorpayMode'] ?? ''));
      this._googleClientId.set(String((d as Record<string, unknown>)['googleClientId'] ?? ''));
      this._facebookAppId.set(String((d as Record<string, unknown>)['facebookAppId'] ?? ''));
    } catch (e) {
      console.warn('public-config load failed — payment/social login stay in fallback mode', e);
    }
  }
}
